import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Alert,
  Chip,
  Divider,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface CallAnalysis {
  id: number;
  recording_id?: number;
  filename: string;
  original_filename?: string;
  status: string;
  overall_score?: number;
  closing_score?: number;
  connection_score?: number;
  objection_handling_score?: number;
  tone_confidence_score?: number;
  closing_process_score?: number;
  compliance_score?: number;
  performanceCategory?: { category: string; color: string };
  call_date: string;
  customer_type?: string;
  call_outcome?: string;
  transcript?: string;
  transcript_text?: string;
  formatted_transcript?: string;
  speaker_labels?: any;
  analysis_result?: any;
  colorCodedFeedback?: { green: any[]; yellow: any[]; red: any[] };
  coaching_suggestions?: string;
  created_at: string;
  updated_at?: string;
  processed_at?: string;
  agent_name?: string;
  agent_email?: string;
  agent_department?: string;
  department?: string;
  agent?: {
    id: number;
    username: string;
    email: string;
    department?: string;
  };
}

interface User {
  id: number;
  username: string;
  email: string;
  department?: string;
  is_active: boolean;
  organizations: Array<{ id: number; name: string }>;
}

const CallQualityAnalyzer: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [agentId, setAgentId] = useState<string>('');
  const [agents, setAgents] = useState<User[]>([]);
  const [callDate, setCallDate] = useState<string>('');
  const [customerType, setCustomerType] = useState<string>('');
  const [callOutcome, setCallOutcome] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState<string>('');
  const [recentAnalyses, setRecentAnalyses] = useState<CallAnalysis[]>([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState<CallAnalysis | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    strengths: true,
    improvements: true,
    compliance: true,
    transcript: false,
  });
  const [tabValue, setTabValue] = useState(0);
  const [organizationId, setOrganizationId] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [callToDelete, setCallToDelete] = useState<CallAnalysis | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const storedOrg = localStorage.getItem('selectedOrg');
    if (storedOrg) {
      const org = JSON.parse(storedOrg);
      setOrganizationId(org.id);
      fetchAgents();
      fetchRecentAnalyses();
    }
  }, []);

  // Auto-refresh the list while any analysis is still pending/processing
  useEffect(() => {
    const hasActive = recentAnalyses.some(
      (a) => a.status === 'pending' || a.status === 'processing'
    );
    if (!hasActive) return;

    const interval = setInterval(fetchRecentAnalyses, 5000);
    return () => clearInterval(interval);
  }, [recentAnalyses]);

  // Poll the detail view while the opened analysis is still pending/processing
  useEffect(() => {
    if (!viewDialogOpen || !selectedAnalysis) return;
    if (selectedAnalysis.status !== 'pending' && selectedAnalysis.status !== 'processing') return;

    const recordingId = selectedAnalysis.recording_id || selectedAnalysis.id;
    const interval = setInterval(async () => {
      try {
        const updated = await api.getCallAnalysis(recordingId);
        setSelectedAnalysis(updated);
        if (updated.status === 'completed' || updated.status === 'failed') {
          fetchRecentAnalyses();
        }
      } catch (err) {
        console.error('Polling analysis status failed:', err);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [viewDialogOpen, selectedAnalysis?.status]);

  const fetchAgents = async () => {
    try {
      const response = await api.getUsers();
      if (response.success && response.users) {
        const directMedsAgents = response.users.filter((user: User) => 
          user.organizations.some(org => org.name === 'Direct Meds') && user.is_active
        );
        setAgents(directMedsAgents);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const fetchRecentAnalyses = async () => {
    try {
      const analyses = await api.getAllCallAnalyses();
      setRecentAnalyses(Array.isArray(analyses) ? analyses : []);
    } catch (error) {
      console.error('Error fetching analyses:', error);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/mp4', 'audio/m4a', 'audio/ogg', 'audio/webm'];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|m4a|mp4|ogg|webm)$/i)) {
        setError('Please select a valid audio file (mp3, wav, m4a, mp4, ogg, webm)');
        return;
      }
      
      setSelectedFile(file);
      setError('');
      setUploadSuccess(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !agentId || !organizationId) {
      setError('Please select a file and agent');
      return;
    }

    setUploading(true);
    setError('');
    setUploadSuccess(false);

    try {
      const metadata = {
        agentId: parseInt(agentId),
        organizationId,
        callDate: callDate || undefined,
        customerType: customerType || undefined,
        callOutcome: callOutcome || undefined,
        notes: notes || undefined,
      };

      const result = await api.uploadCallRecording(selectedFile, metadata);
      
      setUploadSuccess(true);
      setSelectedFile(null);
      setAgentId('');
      setCallDate('');
      setCustomerType('');
      setCallOutcome('');
      setNotes('');

      alert('Call uploaded successfully! Analysis in progress...');
      await fetchRecentAnalyses();
      setTabValue(1);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleViewAnalysis = async (analysis: CallAnalysis) => {
    try {
      const recordingId = analysis.recording_id || analysis.id;
      const fullAnalysis = await api.getCallAnalysis(recordingId);
      console.log('Full Analysis Data:', fullAnalysis);
      setSelectedAnalysis(fullAnalysis);
      setViewDialogOpen(true);
    } catch (error) {
      console.error('Error fetching analysis:', error);
      setError('Failed to load analysis details');
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, analysis: CallAnalysis) => {
    e.stopPropagation();
    setCallToDelete(analysis);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!callToDelete) return;

    setDeleting(true);
    try {
      const id = callToDelete.recording_id || callToDelete.id;
      await api.deleteCallAnalysis(id);
      await fetchRecentAnalyses();
      setDeleteConfirmOpen(false);
      setCallToDelete(null);
      alert('✅ Call analysis deleted successfully!');
    } catch (error) {
      console.error('Error deleting:', error);
      alert('❌ Failed to delete call analysis. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getScoreColor = (score: number, maxScore: number): string => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return '#4caf50';
    if (percentage >= 60) return '#ff9800';
    return '#f44336';
  };

  const getOutcomeLabel = (outcome?: string, department?: string): string => {
    const isCS = department === 'Customer Service';
    
    if (isCS) {
      switch(outcome) {
        case 'resolved': return 'Resolved';
        case 'saved': return 'Saved';  
        case 'cancelled': return 'Cancelled';
        case 'escalated': return 'Escalated';
        case 'follow_up': return 'Follow-up';
        case 'unresolved': return 'Unresolved';
        default: return 'Unknown';
      }
    } else {
      switch(outcome) {
        case 'sale': return 'Won';
        case 'follow_up': return 'Follow-up';
        case 'lost': return 'Lost';
        default: return 'Unknown';
      }
    }
  };

  const getOutcomeColor = (outcome?: string, department?: string): 'default' | 'success' | 'error' | 'warning' | 'info' => {
    const isCS = department === 'Customer Service';
    
    if (isCS) {
      switch(outcome) {
        case 'resolved':
        case 'saved': return 'success';
        case 'cancelled': return 'error';
        case 'escalated': return 'warning';
        case 'follow_up': return 'info';
        default: return 'default';
      }
    } else {
      switch(outcome) {
        case 'sale': return 'success';
        case 'follow_up': return 'info';
        case 'lost': return 'error';
        default: return 'default';
      }
    }
  };

  const ScoreGauge: React.FC<{ score: number; maxScore: number; label: string }> = ({ score, maxScore, label }) => {
    const percentage = (score / maxScore) * 100;
    const color = getScoreColor(score, maxScore);

    return (
      <Box sx={{ textAlign: 'center', p: 2 }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant="determinate"
            value={percentage}
            size={100}
            thickness={4}
            sx={{ color: color }}
          />
          <Box
            sx={{
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
              {score}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              / {maxScore}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
          {label}
        </Typography>
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#61ABBC' }}>
        📞 Call Quality Analyzer
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Upload sales call recordings for AI-powered sentiment analysis and quality scoring
      </Typography>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab label="Upload Call" />
        <Tab label="Recent Analyses" />
      </Tabs>

      {/* Upload Tab */}
      {tabValue === 0 && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Upload Call Recording
                </Typography>

                <Box sx={{ mt: 3 }}>
                  <input
                    accept="audio/*,.mp3,.wav,.m4a,.mp4,.ogg,.webm"
                    style={{ display: 'none' }}
                    id="audio-file-upload"
                    type="file"
                    onChange={handleFileSelect}
                  />
                  <label htmlFor="audio-file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      startIcon={<UploadIcon />}
                      sx={{ mb: 2, py: 2 }}
                    >
                      {selectedFile ? selectedFile.name : 'Select Audio File'}
                    </Button>
                  </label>

                  {selectedFile && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      File selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </Alert>
                  )}

                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Select Agent *</InputLabel>
                    <Select
                      value={agentId}
                      onChange={(e: SelectChangeEvent) => setAgentId(e.target.value)}
                      label="Select Agent *"
                    >
                      {agents.map((agent) => (
                        <MenuItem key={agent.id} value={agent.id}>
                          {agent.username}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  {uploadSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Call uploaded successfully! Analysis in progress...
                    </Alert>
                  )}

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleUpload}
                    disabled={!selectedFile || !agentId || uploading}
                    sx={{ py: 1.5, bgcolor: '#61ABBC', '&:hover': { bgcolor: '#4B88A2' } }}
                  >
                    {uploading ? <CircularProgress size={24} /> : 'Upload & Analyze'}
                  </Button>

                  {uploading && (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                        Processing... This may take 30-60 seconds
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📊 What We Analyze
                </Typography>
                
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold', color: '#1976d2' }}>
                  💼 SALES CALLS (100 pts)
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Customer Connection (20 pts)"
                      secondary="Rapport building, greeting, active listening"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Objection Handling (20 pts)"
                      secondary="Confidence in addressing concerns"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Tone & Confidence (20 pts)"
                      secondary="Professional, confident communication"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Closing Process (30 pts)"
                      secondary="Portal link, support numbers, all steps"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><WarningIcon color="warning" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Compliance (10 pts)"
                      secondary="No prohibited language"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Box>
        </Stack>
      )}

      {/* Recent Analyses Tab */}
      {tabValue === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Recent Call Analysis
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                },
                gap: 2,
                mt: 1,
              }}
            >
              {recentAnalyses.map((analysis) => (
                <Paper
                  key={analysis.recording_id || analysis.id}
                  sx={{
                      p: 2,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#f5f5f5' },
                      borderLeft: `4px solid ${analysis.performanceCategory?.color === 'yellow' ? '#ff9800' : analysis.performanceCategory?.color || '#9e9e9e'}`,
                  }}
                  onClick={() => handleViewAnalysis(analysis)}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {analysis.agent_name || analysis.agent?.username} - {analysis.agent_department || analysis.department || 'Sales'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleDeleteClick(e, analysis)}
                          sx={{ color: '#f44336' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        {analysis.overall_score !== undefined && analysis.overall_score !== null && (
                          <>
                            <Chip
                              label={`${analysis.overall_score}/100`}
                              size="small"
                              sx={{
                                bgcolor: analysis.performanceCategory?.color === 'yellow' ? '#ff9800' : analysis.performanceCategory?.color,
                                color: 'white',
                                fontWeight: 'bold',
                              }}
                            />
                            <Chip
                              label={getOutcomeLabel(analysis.call_outcome, analysis.agent_department || analysis.department || 'Sales')}
                              size="small"
                              color={getOutcomeColor(analysis.call_outcome, analysis.agent_department || analysis.department || 'Sales')}
                            />
                          </>
                        )}
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(analysis.processed_at || analysis.updated_at || analysis.created_at).toLocaleString()}
                    </Typography>
                    {analysis.performanceCategory && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {analysis.performanceCategory.category}
                      </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Analysis Detail Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        {selectedAnalysis && selectedAnalysis.overall_score !== null && selectedAnalysis.overall_score !== undefined && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Call Analysis - {selectedAnalysis.agent_name} - {selectedAnalysis.department || selectedAnalysis.agent_department || 'Sales'}
                </Typography>
                {selectedAnalysis.performanceCategory && (
                  <Chip
                    label={selectedAnalysis.performanceCategory.category}
                    sx={{
                      bgcolor: selectedAnalysis.performanceCategory.color === 'yellow' ? '#ff9800' : selectedAnalysis.performanceCategory.color,
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                )}
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {/* Overall Score with Outcome */}
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  <Typography variant="h3" sx={{ fontWeight: 'bold', color: getScoreColor(selectedAnalysis.overall_score, 100) }}>
                    {selectedAnalysis.overall_score}/100
                  </Typography>
                  <Chip
                    label={getOutcomeLabel(selectedAnalysis.call_outcome, selectedAnalysis.department || selectedAnalysis.agent_department || 'Sales')}
                    color={getOutcomeColor(selectedAnalysis.call_outcome, selectedAnalysis.department || selectedAnalysis.agent_department || 'Sales')}
                    sx={{ fontSize: '1.2rem', fontWeight: 'bold', py: 2.5 }}
                  />
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                  Overall Score
                </Typography>
              </Box>

              {/* Score Breakdown */}
              {selectedAnalysis.connection_score !== undefined && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 2,
                    mb: 4,
                  }}
                >
                  <ScoreGauge
                    score={selectedAnalysis.connection_score || 0}
                    maxScore={20}
                    label="Connection"
                  />
                  <ScoreGauge
                    score={selectedAnalysis.objection_handling_score || 0}
                    maxScore={20}
                    label="Objections"
                  />
                  <ScoreGauge
                    score={selectedAnalysis.tone_confidence_score || 0}
                    maxScore={20}
                    label="Tone"
                  />
                  <ScoreGauge
                    score={selectedAnalysis.closing_process_score || 0}
                    maxScore={30}
                    label="Closing"
                  />
                  <ScoreGauge
                    score={selectedAnalysis.compliance_score || 0}
                    maxScore={10}
                    label="Compliance"
                  />
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Color-Coded Feedback */}
              {selectedAnalysis.colorCodedFeedback && (
                <>
                  {/* Strengths (Green) */}
                  {selectedAnalysis.colorCodedFeedback.green.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1 }}
                        onClick={() => toggleSection('strengths')}
                      >
                        <CheckIcon sx={{ color: '#4caf50', mr: 1 }} />
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                          ✅ Strengths - What They Did Well
                        </Typography>
                        {expandedSections.strengths ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </Box>
                      <Collapse in={expandedSections.strengths}>
                        <Paper sx={{ p: 2, bgcolor: '#f1f8f4' }}>
                          {selectedAnalysis.colorCodedFeedback.green.map((item: any, idx: number) => (
                            <Box key={idx} sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" fontWeight="bold" color="#2e7d32">
                                {item.category}
                              </Typography>
                              <Typography variant="body2">{item.description}</Typography>
                              {item.example && (
                                <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                  Example: "{item.example}"
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Paper>
                      </Collapse>
                    </Box>
                  )}

                  {/* Improvements (Yellow) */}
                  {selectedAnalysis.colorCodedFeedback.yellow.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1 }}
                        onClick={() => toggleSection('improvements')}
                      >
                        <WarningIcon sx={{ color: '#ff9800', mr: 1 }} />
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                          ⚠️ Room for Improvement
                        </Typography>
                        {expandedSections.improvements ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </Box>
                      <Collapse in={expandedSections.improvements}>
                        <Paper sx={{ p: 2, bgcolor: '#fff8e1' }}>
                          {selectedAnalysis.colorCodedFeedback.yellow.map((item: any, idx: number) => (
                            <Box key={idx} sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" fontWeight="bold" color="#ed6c02">
                                {item.category}
                              </Typography>
                              <Typography variant="body2">{item.issue}</Typography>
                              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                                💡 Suggestion: {item.suggestion}
                              </Typography>
                            </Box>
                          ))}
                        </Paper>
                      </Collapse>
                    </Box>
                  )}

                  {/* Critical Issues (Red) */}
                  {selectedAnalysis.colorCodedFeedback.red.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1 }}
                        onClick={() => toggleSection('compliance')}
                      >
                        <ErrorIcon sx={{ color: '#f44336', mr: 1 }} />
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                          🚨 Critical Issues & Compliance
                        </Typography>
                        {expandedSections.compliance ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </Box>
                      <Collapse in={expandedSections.compliance}>
                        <Paper sx={{ p: 2, bgcolor: '#ffebee' }}>
                          {selectedAnalysis.colorCodedFeedback.red.map((item: any, idx: number) => (
                            <Box key={idx} sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" fontWeight="bold" color="#d32f2f">
                                {item.category}
                              </Typography>
                              <Typography variant="body2">{item.issue}</Typography>
                              {item.quote && (
                                <Typography variant="caption" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                  Quote: "{item.quote}"
                                </Typography>
                              )}
                              {item.impact && (
                                <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>
                                  Impact: {item.impact}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Paper>
                      </Collapse>
                    </Box>
                  )}
                </>
              )}

              {/* Coaching Suggestions */}
              {selectedAnalysis.coaching_suggestions && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    💬 Coaching Feedback
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedAnalysis.coaching_suggestions}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Transcript */}
              {(selectedAnalysis.formatted_transcript || selectedAnalysis.transcript_text || selectedAnalysis.transcript) && (
                <Box>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1 }}
                    onClick={() => toggleSection('transcript')}
                  >
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      📝 Call Dialogue {selectedAnalysis.formatted_transcript && '(Agent vs Customer)'}
                    </Typography>
                    {expandedSections.transcript ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                  <Collapse in={expandedSections.transcript}>
                    <Paper sx={{ p: 2, bgcolor: '#fafafa', maxHeight: 400, overflow: 'auto' }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', lineHeight: 1.8 }}>
                        {selectedAnalysis.formatted_transcript || selectedAnalysis.transcript_text || selectedAnalysis.transcript}
                      </Typography>
                    </Paper>
                  </Collapse>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
        {selectedAnalysis && (selectedAnalysis.status === 'processing' || selectedAnalysis.status === 'pending') && (
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Analysis in Progress...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This usually takes 30-60 seconds. This view will update automatically.
              </Typography>
            </Box>
          </DialogContent>
        )}
        {selectedAnalysis && selectedAnalysis.status === 'failed' && (
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="error">
                Analysis Failed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The call could not be processed. Please try uploading it again.
              </Typography>
            </Box>
          </DialogContent>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Delete Call Analysis?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this call analysis? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CallQualityAnalyzer;
